import { json, redirect, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { fetchJobPost } from "~/utils/fetch_job_post";
import { JobPost } from "~/components/job_posts/job_post";
import { ApplicationForm } from "~/components/job_posts/application_form";
import { Logo } from "~/components/logo";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { buildJobPostTags } from "~/utils/job_helpers";
import { useRef, useEffect } from "react";
import { focusForm } from "~/utils/focus_form";
import { isClient } from "~/utils/env_check";
import { loadGooglePicker } from "~/utils/load_google_picker";
import useRecruitics from "~/hooks/use_recruitics";
import { withNamespace } from "~/utils/translation";
import config from "~/config.server";
import { useTrackingParams } from "~/hooks/use_tracking_params";
import { QuickApply } from "~/quick_apply/quick_apply";
import { ApplyWithSeek } from "~/apply_with_seek/apply_with_seek";
import { createPostOpenGraphTags } from "~/utils/build_page_metadata";
import useAppcast, { AppcastEvent } from "~/hooks/use_appcast";
import { loadSeekData } from "~/apply_with_seek/utils/seek_loader.server";
import { handleSeekAuthCallback } from "~/apply_with_seek/utils/seek_auth_tab.client";
import { fetchBoardConfiguration } from "~/utils/fetch_board_configuration";

type Params = {
  url_token: string;
  job_post_id: string;
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { url_token: urlToken, job_post_id: jobPostId } = params as Params;
  const currentUrl = new URL(request.url);

  const { jobPost, status: jobApplicationStatus } = await fetchJobPost({
    urlToken,
    jobPostId,
    apiVersion: "v2",
    includeQuestions: true,
    includeEducation: true,
  });

  if (jobApplicationStatus === 404 || !jobPost) {
    return redirect(`/${urlToken}?error=true`);
  }

  if (jobPost.redirect_to) {
    if (jobPost.redirect_to.startsWith("/")) {
      return redirect(`${config.get("domains.jben_url")}${jobPost.redirect_to}`);
    }
    return redirect(jobPost.redirect_to);
  }

  if (!jobPost.questions) {
    return redirect(`/${urlToken}?error=true`);
  }

  const confirmationPath = `/${urlToken}/jobs/${jobPostId}/confirmation`;
  const { configuration: boardConfig } = await fetchBoardConfiguration({ urlToken });
  const seekId = boardConfig.seek_id;
  const seekData = await loadSeekData(request, {
    seekId,
    jobPostId,
    applyWithSeek: jobPost.apply_with_seek,
  });

  return json({
    jobPost,
    jobPostId,
    urlToken,
    submitPath: config.get("domains.jben_url") + currentUrl.pathname,
    confirmationPath,
    quickApply: {
      active: config.get("quickApply.active") as boolean,
      magicActive: config.get("quickApply.magicActive") as boolean,
      url: config.get("job_seekers.url") as string,
      metadata: {
        jobPostId: +jobPostId,
      },
    },
    ...seekData,
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const t = withNamespace("job_post");

  if (!data) {
    return [];
  }

  const { title, company_name: companyName } = data.jobPost;
  return [
    {
      title: t("page_title", {
        title,
        companyName,
        interpolation: { escapeValue: false },
      }),
    },
    ...createPostOpenGraphTags(data.jobPost),
  ];
};

export const handle = { i18n: "job_post" };

let loadedGooglePicker: boolean;

export default function ExternalJobPost() {
  const { jobPost, urlToken, submitPath, confirmationPath, quickApply } =
    useLoaderData<typeof loader>();
  const {
    allow_external_notifications: allowExternalNotifications,
    enable_recruitics: enableRecruitics,
    appcast_tracking_code: appcastTrackingCode,
    logo,
    google_drive_enabled,
    display_new_jobs_tag: displayNewJobsTag,
  } = useBoardConfiguration();
  const { withTrackingParams } = useTrackingParams();

  const formRef = useRef<HTMLFormElement>(null);

  const shouldLoadGooglePicker = isClient() && google_drive_enabled;
  const quickApplyActive = quickApply.active && jobPost.show_quick_apply_cta;

  if (!loadedGooglePicker && shouldLoadGooglePicker) {
    loadGooglePicker();
    loadedGooglePicker = true;
  }

  useRecruitics(enableRecruitics);
  useAppcast(AppcastEvent.JobView, appcastTrackingCode);

  // Handle Seek auth callback if page loads in popup
  useEffect(() => {
    handleSeekAuthCallback();
  }, []);

  return (
    <QuickApply
      active={quickApplyActive}
      magicActive={quickApply.magicActive}
      url={quickApply.url}
      metadata={quickApply.metadata}
    >
      <ApplyWithSeek>
        <div className="job-post-container">
          <Logo
            companyName={jobPost.company_name}
            url={logo.url}
            href={logo.href && withTrackingParams(logo.href)}
          />
          <JobPost
            content={jobPost.content}
            conclusion={jobPost.conclusion}
            introduction={jobPost.introduction}
            location={jobPost.job_post_location}
            title={jobPost.title}
            payRanges={jobPost.pay_ranges}
            tags={buildJobPostTags(jobPost, displayNewJobsTag, true)}
            onApplyClick={() => focusForm(formRef.current)}
            hiringPlanId={jobPost.hiring_plan_id}
            urlToken={urlToken}
            companyName={jobPost.company_name}
            allowJobAlert={allowExternalNotifications}
          />
          <div className="divider" role="separator"></div>
          <ApplicationForm
            jobPost={jobPost}
            demographicSection={jobPost.demographic_questions}
            questions={jobPost.questions}
            submitPath={submitPath}
            confirmationPath={withTrackingParams(confirmationPath)}
            formRef={formRef}
          />
        </div>
      </ApplyWithSeek>
    </QuickApply>
  );
}
